<script>
import {
  ref, onMounted, watch, computed,
} from 'vue';

import { useI18n } from 'vue-i18n';

import { useBreakpoints, breakpointsTailwind } from '@vueuse/core';

import { BubbleQuestionIcon } from '@thehotelsnetwork/icons/vue/linear';

// Components
import CalendarMonth from './CalendarMonth.vue';
import HeatDay from './Days/HeatDay.vue';
import CalendarLegend from './CalendarLegend.vue';
import DayInfoModal from './DayInfoModal.vue';

import BmMetricDescriptionBox from '@/components/MetricDescriptionBox/MetricDescriptionBox.vue';
import BmLoader from '@/components/Loader/Loader.vue';
import ModalBox from '@/components/ModalBox/ModalBox.vue';
import UpgradeBtn from '@/components/UpgradeBtn/UpgradeBtn.vue';
import ProductTourBtn from '@/components/ProductTourBtn/ProductTourBtn.vue';

// States
import useCalendarViewState from '@/state/useCalendarViewState';
import useSectionState from '@/state/useSectionState';
import useUserState from '@/state/useUserState';
import useFilterState from '@/state/useFilterState';

// Filters
import {
  selectedMetricConfig,
} from '@/config/sectionFiltersConfig';

// Directives
import scrollReveal from '@/directives/scroll-reveal';

// Utils
import prepareDayModalData from '@/utils/prepareDayModalData';
import getStats from '@/utils/getStats';

// Config
import {
  CALENDAR_TOUR,
} from '@/modules/productTour';

export default {
  name: 'CalendarSection',
  components: {
    BubbleQuestionIcon,
    CalendarMonth,
    CalendarLegend,
    HeatDay,
    BmMetricDescriptionBox,
    BmLoader,
    ModalBox,
    DayInfoModal,
    UpgradeBtn,
    ProductTourBtn,
  },
  directives: {
    scrollReveal,
  },
  setup() {
    const { t } = useI18n();
    const {
      selectedMetric,
    } = useFilterState();
    const {
      pipeData,
      calendarData,
      isValidCalendarData,
      selectedFirstCompsetTheme,
      selectedSecondCompsetTheme,
      calendarMetricDataFormat,
      currentSizeMeasureConfig,
      calendarStatsType,
    } = useCalendarViewState();
    const {
      currentPermissions,
      sectionHasCalendarStayDates,
      sectionMetrics,
    } = useSectionState();
    const {
      userData,
      setUserData,
    } = useUserState();

    const disableStayDates = computed(() => !sectionHasCalendarStayDates.value
      || !!sectionMetrics.value
        .filter((metric) => metric.name === selectedMetric.value)[0]?.disableCalendarStayDates);

    const calendarStatsOptions = computed(() => [
      { value: 'over-time', label: t('Browsing Dates') },
      { value: 'stay-dates', label: t('Stay Dates'), disabled: disableStayDates.value },
    ]);

    const calendarSectionRef = ref(null);
    const showHowToReadTT = ref(false);
    const showModal = ref(false);
    const lgOrSmaller = useBreakpoints(breakpointsTailwind).smaller('xl');
    const modalData = ref(null);
    const tpConfig = {};

    const toggleModal = () => {
      showModal.value = !showModal.value;
    };

    const handleCalendarModal = (data) => {
      if (!data || !data.isValid) {
        modalData.value = null;
        return;
      }

      toggleModal();

      modalData.value = prepareDayModalData({
        data,
        minSize: calendarData.value.minSize,
        maxSize: calendarData.value.maxSize,
        currentSizeMeasureConfig: currentSizeMeasureConfig.value,
        barChartFormat: calendarMetricDataFormat.value,
      });
    };

    onMounted(() => {
      /*
        Watch changes in currentpercurrentPermissions (Debug only)
        Will trigger getStats when changing the plan value under calendar view
      */
      watch(calendarStatsType, () => {
        getStats({ reset: true });
      });

      watch(selectedMetric, () => {
        if (calendarStatsType.value === 'stay-dates' && disableStayDates.value) {
          calendarStatsType.value = 'over-time';
        }
      });
    });

    return {
      calendarData,
      isValidCalendarData,
      calendarSectionRef,
      selectedMetric,
      selectedFirstCompsetTheme,
      selectedSecondCompsetTheme,
      pipeData,
      selectedMetricConfig,
      tpConfig,
      handleCalendarModal,
      showModal,
      calendarStatsOptions,
      lgOrSmaller,
      showHowToReadTT,
      toggleModal,
      modalData,
      calendarStatsType,
      currentPermissions,
      disableStayDates,
      userData,
      setUserData,
      calendarMetricDataFormat,
      currentSizeMeasureConfig,
      CALENDAR_TOUR,
    };
  },
};
</script>
<template>
  <div
    ref="calendarSectionRef"
    class="mt-4"
  >
    <LpCard
      v-if="!currentPermissions.calendar"
      class="flex items-center gap-4 p-4 mt-2 font-normal shadow-none bg-grape-100 text-grape-500"
    >
      <div>
        <div class="text-lg font-bold font-display">
          {{ $t('Identify new opportunities with the Calendar view.') }}
        </div>
        <div
          v-html="$t('Go Pro to compare your hotel\'s daily performance with the compset of your choice.<i>This data belongs to a test hotel.</i>')"
        />
      </div>
      <UpgradeBtn section="calendar" />
    </LpCard>
    <LpCard
      v-scroll-reveal
      class="px-12 py-10 mt-10 bm-card wrapper-highlight"
    >
      <!-- Loader -->
      <BmLoader
        v-if="currentPermissions.calendar && pipeData.loading"
        class="calendar-loader"
        :size="'big'"
      />
      <!-- Loaded Content -->
      <template
        v-else
      >
        <div class="flex items-center justify-between">
          <BmMetricDescriptionBox
            :metric="selectedMetricConfig.placeholder"
            :tp-config="tpConfig"
          />
          <div class="flex items-center">
            <LpTooltip
              v-model="showHowToReadTT"
              size="small"
              placement="top"
              mode="manual"
              @mouseover="showHowToReadTT = lgOrSmaller"
              @mouseleave="showHowToReadTT = false"
            >
              <template #trigger>
                <LpButton
                  type="button"
                  size="medium"
                  mode="transparent"
                  color="gray"
                  class="mr-4"
                >
                  <div class="flex items-center">
                    <ProductTourBtn :flow-id="CALENDAR_TOUR">
                      <BubbleQuestionIcon class="w-6 h-6 xl:mr-2" />
                      <span class="text-sm font-bold sm:hidden xl:inline-block">
                        {{ $t('How to read this view') }}
                      </span>
                    </ProductTourBtn>
                  </div>
                </LpButton>
              </template>
              <template
                #tooltip
              >
                {{ $t('How to read this view') }}
              </template>
            </LpTooltip>
            <LpButtonGroup
              v-model="calendarStatsType"
              :options="calendarStatsOptions"
            >
              <template #tooltip-stay-dates>
                {{ $t('This metric doesn\'t relate to stay dates') }}
              </template>
            </LpButtonGroup>
          </div>
        </div>
        <!-- Calendar -->
        <div
          v-if="calendarData && isValidCalendarData"
        >
          <CalendarLegend
            :min-neg-range="calendarData.minNegativeDiff"
            :max-neg-range="calendarData.maxNegativeDiff"
            :min-pos-range="calendarData.minPositiveDiff"
            :max-pos-range="calendarData.maxPositiveDiff"
            :min-size-range="calendarData.minSize"
            :max-size-range="calendarData.maxSize"
            :theme-first="selectedFirstCompsetTheme"
            :theme-second="selectedSecondCompsetTheme"
            :format="currentSizeMeasureConfig.format"
            :measure-text="currentSizeMeasureConfig.measureText"
          />
          <div class="calendar">
            <div
              v-for="(days, timestamp) in calendarData.valuesByMonth"
              :key="timestamp"
              class="month"
            >
              <CalendarMonth
                :month="Number(timestamp)"
                :year="Number(timestamp)"
                first-day-of-week="mo"
              >
                <template #header="{ monthName, currentYear }">
                  <div class="flex items-center justify-start py-4 capitalize">
                    {{ monthName }} {{ currentYear }}
                  </div>
                </template>
                <template #days="{ day }">
                  <HeatDay
                    :radius="(days[day] || {}).radius"
                    :theme="(days[day] || {}).theme"
                    :opacity="(days[day] || {}).opacity"
                    @click="handleCalendarModal(days[day])"
                  >
                    {{ day }}
                  </HeatDay>
                </template>
              </CalendarMonth>
            </div>
          </div>
        </div>
        <!-- No Data  -->
        <div
          v-else
          class="flex flex-col items-center justify-center mt-8 mb-2"
        >
          <img
            src="@/assets/img/icons/common/no-matches-found-icon.svg"
            alt=""
          >
          <h1 class="mt-6 text-2xl font-bold">
            {{ $t('No data found for this time period') }}
          </h1>
          <p class="mt-2 text-xl font-medium text-medium">
            {{ $t('Try a wider data range') }}
          </p>
        </div>
      </template>
    </LpCard>

    <teleport
      v-if="showModal && modalData"
      to="body"
    >
      <ModalBox
        :on-close="toggleModal"
      >
        <DayInfoModal
          :data="modalData"
        />
      </ModalBox>
    </teleport>
  </div>
</template>
<style scoped>
.calendar {
  display: grid;
  grid-template-columns: minmax(26rem, 1fr);
  gap: 2.5rem;
}
.calendar .month { min-width: 26rem;}
.calendar-loader {
  min-height: 35rem;
}
@screen xl {
  .calendar {
    grid-template-columns: repeat(2, minmax(26rem, 1fr));
  }
}
</style>
